/**
 * 保留トレイのケース詳細 (3択 確定画面)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.3
 */
import type { ConfidenceFactor } from '@goenhub/shared';
import { ArrowLeft, Inbox, Tag } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfidenceBadge } from '@/components/deals/ConfidenceBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDeals } from '@/hooks/useDeals';
import {
  useDealMergeCandidate,
  useResolveMergeCandidate,
} from '@/hooks/useDealMergeCandidates';
import { useMailContacts } from '@/hooks/useMailContacts';

function dateLabel(v: unknown): string {
  if (!v) return '—';
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : `${d.getMonth() + 1}/${d.getDate()}`;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    const d = (v as { toDate: () => Date }).toDate();
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return '—';
}

function FactorChip({ factor }: { factor: ConfidenceFactor }) {
  const variant: 'approved' | 'draft' | 'default' =
    factor.tone === 'ok' ? 'approved' : factor.tone === 'warn' ? 'draft' : 'default';
  const sign = factor.value > 0 ? '+' : '';
  return (
    <Badge variant={variant}>
      <span>{factor.label}</span>
      <span className="ml-1 tabular-nums opacity-80">
        {sign}
        {Math.round(factor.value * 100) / 100}
      </span>
    </Badge>
  );
}

export function MergeCandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: candidate, isLoading } = useDealMergeCandidate(id);
  // 全連絡先を取得 (limit を付けると枠外の連絡先名が解決できず顧客不明になる)
  const { data: contacts } = useMailContacts();
  const { data: deals } = useDeals();
  const resolveMutation = useResolveMergeCandidate();

  if (isLoading || !candidate) {
    return (
      <Screen title="統合判断">
        {isLoading ? (
          <Spinner />
        ) : (
          <EmptyState
            icon={Inbox}
            title="ケースが見つかりません"
            description="削除されたか、URL が誤っている可能性があります。"
          />
        )}
      </Screen>
    );
  }

  const contact = contacts?.find((c) => c.id === candidate.contactId);
  const isResolved = !!candidate.resolvedAt;

  const handleAction = async (action: 'merge_to_existing' | 'create_new' | 'ignore', targetDealId?: string) => {
    if (!id) return;
    try {
      const res = await resolveMutation.mutateAsync({
        candidateId: id,
        action,
        targetDealId,
      });
      if (action === 'merge_to_existing') {
        toast.success('既存 Deal にマージしました');
      } else if (action === 'create_new') {
        toast.success('新規 Deal を作成しました');
      } else {
        toast.success('無視として記録しました');
      }
      if (res.resolvedDealId) {
        navigate(`/deals/${res.resolvedDealId}`);
      } else {
        navigate('/deals/inbox');
      }
    } catch (err) {
      toast.error(
        `確定失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const sortedCandidates = [...candidate.candidates].sort(
    (a, b) => b.confidence - a.confidence,
  );
  const bestId = sortedCandidates[0]?.dealId;

  return (
    <Screen
      title="統合判断 / ケース詳細"
      description="候補のスコア根拠を比較し、合体・新規・無視を選択"
      actions={
        <Button type="button" variant="ghost" onClick={() => navigate('/deals/inbox')}>
          <ArrowLeft />
          保留トレイへ戻る
        </Button>
      }
    >
      {/* 顧客ヘッダー */}
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-medium">
            {contact?.name ?? '(顧客名未確定)'}
          </span>
          {contact && (
            <Badge variant={contact.kind === 'btob' ? 'accent' : 'review'}>
              {contact.kind === 'btob' ? 'BtoB' : contact.kind === 'btoc' ? 'BtoC' : 'Unknown'}
            </Badge>
          )}
          <Badge variant="default">contactId 同定済</Badge>
          {isResolved && <Badge variant="approved">確定済</Badge>}
        </div>
        <div className="text-xs text-[var(--text-secondary)] mt-1">
          {contact?.defaultEmail ?? '(メール不明)'}
        </div>
      </div>

      {/* マージ候補 */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
            マージ候補
          </span>
          <span className="text-xs text-[var(--text-muted)]">
            {sortedCandidates.length} 件 · スコアリング根拠付き
          </span>
        </div>
        <div className="flex flex-col gap-3">
          {sortedCandidates.map((cand) => {
            const isBest = cand.dealId === bestId;
            const deal = deals?.find((d) => d.id === cand.dealId);
            return (
              <div
                key={cand.dealId}
                className={`rounded-md p-4 ${
                  isBest
                    ? 'border-2 border-[var(--accent)] bg-[var(--bg-surface)]'
                    : 'border border-[var(--border)] bg-[var(--bg-surface)]'
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium font-mono">
                        {cand.dealId.slice(0, 8)}
                      </span>
                      <span className="text-xs text-[var(--text-secondary)]">
                        · stage: {cand.dealStage}
                      </span>
                      {isBest && <Badge variant="accent">最有力</Badge>}
                    </div>
                    {deal && (
                      <div className="text-xs text-[var(--text-primary)] mt-1 truncate">
                        {deal.title}
                      </div>
                    )}
                    <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-2 mt-1">
                      <Tag className="size-3" />
                      {deal?.primaryCategory ?? '(カテゴリ未確定)'} · {dateLabel(cand.dealLastTouchedAt)}
                    </div>
                  </div>
                  <ConfidenceBadge confidence={cand.confidence} size="sm" />
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {cand.factors.map((f, i) => (
                    <FactorChip key={i} factor={f} />
                  ))}
                </div>
                <Button
                  type="button"
                  variant={isBest ? 'primary' : 'secondary'}
                  size="sm"
                  className="w-full"
                  onClick={() => handleAction('merge_to_existing', cand.dealId)}
                  disabled={isResolved || resolveMutation.isPending}
                >
                  この商談に合体
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* その他のアクション */}
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4">
        <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2">
          その他のアクション
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={() => handleAction('create_new')}
            disabled={isResolved || resolveMutation.isPending}
          >
            新規Deal作成
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={() => handleAction('ignore')}
            disabled={isResolved || resolveMutation.isPending}
          >
            無視 (重複ノイズ)
          </Button>
        </div>
      </div>
    </Screen>
  );
}
